(function () {
  /**
   * @typedef {Object} RequestConfig
   * @property {string} url Relative path (for example `/orders`), app-proxy path, or absolute URL.
   * @property {"get"|"post"|"put"|"patch"|"delete"} [method="get"] HTTP method.
   * @property {any} [data] Query/body payload passed to jQuery ajax.
   * @property {string} [contentType] Request content type.
   * @property {string} [dataType="json"] Response data type for jQuery ajax.
   */

  /**
   * @typedef {Object} RequestOptions
   * @property {number} [retries=0] Number of retry attempts after first failure.
   */

  /**
   * @typedef {Object} OrdersQuery
   * @property {number|string} [page]
   * @property {number|string} [limit]
   * @property {string} [status]
   */

  /**
   * @typedef {Object} UpcomingChargesQuery
   * @property {number} [page=0]
   * @property {number} [limit=100]
   * @property {string} [todayDate] Date in `YYYY-MM-DD`.
   * @property {Array<string|number>} [excludedProductIds]
   * @property {string[]} [finalStatuses]
   */

  /**
   * @typedef {Object} UpcomingCharge
   * @property {number|string} orderId
   * @property {string} chargeDate
   * @property {string[]} titles
   * @property {number} selectedSubtotal
   */

  /**
   * @typedef {Object} CreditsResult
   * @property {number|null} balance
   * @property {any} raw
   */

  /**
   * @typedef {Object} OrderItemsResult
   * @property {any[]} items
   * @property {any|null} order
   * @property {any|null} billing_notice
   * @property {any} raw
   */

  const BASE_URL = "/apps/backend/v4/api";
  const DEFAULT_EXCLUDED_PRODUCT_IDS = [
    "6711450796089",
    "6711489921081",
    "6966576709689",
  ];
  const DEFAULT_FINAL_STATUSES = ["paid", "archived"];

  const toMoney = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return 0;
    }
    return Number(parsed.toFixed(2));
  };

  const toDateOnly = (value) => {
    if (typeof value !== "string" || value.length === 0) {
      return "";
    }
    return value.split("T")[0];
  };

  const getTodayDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  /**
   * Normalizes any supported endpoint input to a usable URL.
   * @param {string} url
   * @returns {string}
   */
  const buildUrl = (url) => {
    if (typeof url !== "string" || url.length === 0) {
      return BASE_URL;
    }

    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }

    if (url.startsWith("/apps/")) {
      return url;
    }

    if (url.startsWith("/")) {
      return `${BASE_URL}${url}`;
    }

    return `${BASE_URL}/${url}`;
  };

  /**
   * Base AJAX wrapper with retry support.
   * @param {RequestConfig} config
   * @param {RequestOptions} [options]
   * @returns {Promise<any>}
   */
  const request = async (config, options) => {
    const requestConfig = config || {};
    const requestOptions = options || {};
    const retries = Number(requestOptions.retries || 0);

    const ajaxConfig = {
      url: buildUrl(requestConfig.url),
      type: requestConfig.method || "get",
      dataType: requestConfig.dataType || "json",
    };

    if (requestConfig.data !== undefined) {
      ajaxConfig.data = requestConfig.data;
    }

    if (requestConfig.contentType) {
      ajaxConfig.contentType = requestConfig.contentType;
    }

    let attempt = 0;
    while (attempt <= retries) {
      try {
        return await jQuery.ajax(ajaxConfig);
      } catch (error) {
        attempt += 1;
        if (attempt > retries) {
          throw error;
        }
      }
    }

    return null;
  };

  /**
   * Fetches customer subscriptions for the logged-in customer context.
   * @returns {Promise<any[]>}
   */
  const getCustomerSubscriptions = async () => {
    const response = await request(
      { url: "/customer-subscriptions" },
      { retries: 2 },
    );
    return Array.isArray(response?.data) ? response.data : [];
  };

  /**
   * Fetches the logged-in customer's raw Recharge subscriptions (e.g. donations),
   * normalized server-side, optionally filtered by product/variant id. Sister to
   * getCustomerSubscriptions (which returns CGF share rows).
   * @param {{ product_id?: string|number, variant_id?: string|number, status?: 'active'|'all' }} [params]
   * @returns {Promise<any[]>}
   */
  const getRechargeSubscriptions = async (params) => {
    const options = params || {};
    const data = {};
    if (options.product_id !== undefined) data.product_id = String(options.product_id);
    if (options.variant_id !== undefined) data.variant_id = String(options.variant_id);
    if (options.status !== undefined) data.status = String(options.status);

    const response = await request(
      { url: "/recharge-subscriptions", data },
      { retries: 1 },
    );

    return Array.isArray(response?.data) ? response.data : [];
  };

  /**
   * Fetches customer credit balance.
   * @returns {Promise<CreditsResult>}
   */
  const getCredits = async () => {
    const response = await request({ url: "/credits" }, { retries: 2 });
    return {
      balance:
        response?.data?.balance !== undefined
          ? toMoney(response.data.balance)
          : null,
      raw: response,
    };
  };

  /**
   * The member's own credit ledger — the last 10 movements, newest first
   * (plan 0039 §13.1). No paging, by decision D11.
   *
   * Pending rows are included and deliberately NOT marked: the balance on the credit
   * card already folds pending credit in, so the list has to reconcile to it, and a
   * "pending" tag would invite a question the balance itself never raises (D9).
   *
   * Rows arrive pre-labelled and pre-walked by the server: `label` is plain customer
   * wording for rows the close wrote and the staff member's own words for a manual
   * credit, and `balance_after` is the running balance, so the newest row's
   * `balance_after` equals the figure on the card. The theme does no arithmetic and
   * no label mapping of its own.
   *
   * @param {number|string} [before] the previous page's last transaction id ("Show older")
   * @returns {Promise<{transactions: Array<{id: number|string, date: string, amount: number|string, label: string, week_of: string|null, order_id: number|null, balance_after: number|string}>, next_before: number|string|null}>}
   */
  const getCreditTransactions = async (before) => {
    const response = await request(
      { url: before ? `/credits/transactions?before=${encodeURIComponent(before)}` : "/credits/transactions" },
      { retries: 2 },
    );

    // `{ data: { transactions, next_before } }` is the shape; the bare-array forms are the
    // pre-paging server, which never has a next page.
    if (Array.isArray(response?.data?.transactions)) {
      return {
        transactions: response.data.transactions,
        next_before: response.data.next_before ?? null,
      };
    }
    if (Array.isArray(response?.data)) return { transactions: response.data, next_before: null };
    if (Array.isArray(response?.transactions)) {
      return { transactions: response.transactions, next_before: null };
    }
    return { transactions: [], next_before: null };
  };

  /**
   * Fetches customer orders with optional filters.
   * @param {OrdersQuery} [params]
   * @returns {Promise<any[]>}
   */
  const getOrders = async (params) => {
    const options = params || {};
    const data = {};

    if (options.page !== undefined) {
      data.page = String(options.page);
    }
    if (options.limit !== undefined) {
      data.limit = String(options.limit);
    }
    if (options.status !== undefined) {
      data.status = String(options.status);
    }

    const response = await request(
      {
        url: "/orders",
        data,
      },
      { retries: 2 },
    );

    return Array.isArray(response?.data) ? response.data : [];
  };

  /**
   * Fetches one customer order by id.
   * @param {number|string} orderId
   * @returns {Promise<any|null>}
   */
  const getOrder = async (orderId) => {
    const response = await request(
      {
        url: `/orders/${orderId}`,
      },
      { retries: 1 },
    );

    if (response?.order && typeof response.order === "object") {
      return response.order;
    }

    if (
      response?.data &&
      typeof response.data === "object" &&
      !Array.isArray(response.data)
    ) {
      if (response.data.order && typeof response.data.order === "object") {
        return response.data.order;
      }

      return response.data;
    }

    return null;
  };

  /**
   * Skips an order and the entire group of orders for that date.
   * @param {number|string} orderId
   * @returns {Promise<any>}
   */
  const postSkipOrder = async (orderId) => {
    // One retry is safe: the backend skip is idempotent (an already-skipped
    // charge resolves as success server-side).
    return request(
      {
        url: `/orders/${orderId}/skip`,
        method: "post"
      },
      { retries: 1 }
    );
  };

  /**
   * The member's delivery schedule — every week of every active, Recharge-backed share
   * out to the end of its season (plan 0039 §3.2).
   *
   * Weeks beyond the open one are PROJECTIONS. Recharge holds exactly one live charge per
   * subscription and computes the rest from the frequency, so only weeks that already
   * have an order row carry an `order_id`; the rest become real the moment they are
   * skipped. `degraded: true` means Recharge was unreachable — the schedule is unknown,
   * not empty, and the caller must say so rather than showing a member no weeks.
   *
   * @returns {Promise<{subscriptions: any[], degraded: boolean}>}
   */
  const getSchedule = async () => {
    const response = await request({ url: "/schedule" }, { retries: 1 });
    const payload =
      response?.data && typeof response.data === "object" && !Array.isArray(response.data)
        ? response.data
        : response;

    return {
      subscriptions: Array.isArray(payload?.subscriptions)
        ? payload.subscriptions
        : [],
      degraded: payload?.degraded === true,
    };
  };

  /**
   * Skips one or more weeks of one share (§3.3). Dates are `YYYY-MM-DD` and must come
   * from getSchedule() — the server rejects anything not on the delivery schedule.
   *
   * Deliberately NOT retried. The server processes the dates in order and a partial
   * failure leaves the earlier skips in place because they are already real in Recharge,
   * so the per-date results are the only honest account of what happened; a blind retry
   * would report dates as failed that had in fact landed.
   *
   * @param {number|string} customerSubscriptionId
   * @param {string[]|string} dates
   * @returns {Promise<Array<{date: string, ok: boolean, order_id?: number, error?: string}>>}
   */
  const postScheduleSkip = async (customerSubscriptionId, dates) => {
    const response = await request({
      url: "/schedule/skip",
      method: "post",
      contentType: "application/json",
      data: JSON.stringify({
        customer_subscription_id: customerSubscriptionId,
        dates: Array.isArray(dates) ? dates : [dates],
      }),
    });

    if (Array.isArray(response?.results)) {
      return response.results;
    }

    if (Array.isArray(response?.data?.results)) {
      return response.data.results;
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    return [];
  };

  /**
   * Asks cgf_app to have Shopify email the member a secure link to update the card on a
   * Shopify-billed share (plan 0040). Recharge cannot see that card, so the Recharge
   * portal is no use for these shares.
   *
   * Deliberately NOT retried: every successful call sends an email, and the server
   * refuses a second send inside 10 minutes (409, with a message to show the member).
   * A 422 means the share is not Shopify-billed or has no payment method.
   *
   * @param {number|string} customerSubscriptionId
   * @returns {Promise<{sent: boolean, method: {type: string, brand: string|null, lastDigits: string|null}|null}>}
   */
  const sendCardUpdateEmail = async (customerSubscriptionId) => {
    const response = await request({
      url: `/customer-subscriptions/${customerSubscriptionId}/payment-method/update-email`,
      method: "post",
      contentType: "application/json",
      data: JSON.stringify({}),
    });

    return {
      sent: response?.data?.sent === true,
      method: response?.data?.method || null,
    };
  };

  /**
   * @typedef {Object} MemberPaymentMethod
   * @property {string} id Opaque id for this share (never Shopify's vault id); send it back to change.
   * @property {"card"|"shop_pay"|"paypal"|"unknown"} type
   * @property {string|null} brand e.g. "visa", "american_express"; null for Shop Pay.
   * @property {string|null} lastDigits
   * @property {number|null} expiryMonth
   * @property {number|null} expiryYear
   * @property {boolean} current The card the share bills now.
   * @property {boolean} expired
   * @property {boolean} expiringSoon Within 30 days of the end of its expiry month.
   * @property {boolean} inactive Revoked, or an agreement Shopify marks inactive.
   * @property {boolean} selectable Not current, not expired, not inactive.
   */

  /**
   * The cards saved on the member's Shopify customer, for a Shopify-billed share (plan 0040):
   * the share's current card first, then the others. Expired, revoked or inactive cards are left
   * out unless a subscription still uses them (the share's own card, or one another subscription
   * bills); one that is listed can't be chosen. `canRemove` says whether saved cards can be removed
   * at all (it needs a permission the farm's app doesn't have yet): show Remove only when true.
   * A 422 means the share is not billed through Shopify (a Recharge share), not active or has no
   * billing agreement.
   *
   * @param {number|string} customerSubscriptionId
   * @returns {Promise<{methods: MemberPaymentMethod[], canRemove: boolean}>}
   */
  const getPaymentMethods = async (customerSubscriptionId) => {
    const response = await request(
      { url: `/customer-subscriptions/${customerSubscriptionId}/payment-methods` },
      { retries: 1 },
    );
    return {
      methods: Array.isArray(response?.data?.methods) ? response.data.methods : [],
      canRemove: response?.data?.canRemove === true,
    };
  };

  /**
   * Moves a Shopify-billed share onto another of the member's saved cards (an `id` from
   * getPaymentMethods). Deliberately NOT retried: a change that got no answer may have gone
   * through. 409 (this week's billing holds the card until Thursday, or a change a moment ago)
   * and 422 (expired, no longer on file, ...) carry a message written for the member.
   *
   * @param {number|string} customerSubscriptionId
   * @param {string} paymentMethodId
   * @returns {Promise<{changed: boolean, method: MemberPaymentMethod|null, methods: MemberPaymentMethod[]}>}
   */
  const changePaymentMethod = async (customerSubscriptionId, paymentMethodId) => {
    const response = await request({
      url: `/customer-subscriptions/${customerSubscriptionId}/payment-methods`,
      method: "post",
      contentType: "application/json",
      data: JSON.stringify({ paymentMethodId }),
    });

    return {
      changed: response?.data?.changed === true,
      method: response?.data?.method || null,
      methods: Array.isArray(response?.data?.methods) ? response.data.methods : [],
    };
  };

  /**
   * Removes one of the member's saved cards (an `id` from getPaymentMethods) from their Shopify
   * account — only offered when getPaymentMethods said `canRemove` (otherwise the server answers
   * 422 `remove_unavailable`). Deliberately NOT retried: a removal that got no answer may have gone
   * through, and it can't be undone. The server refuses the card the share bills and a card another subscription
   * bills, or may (409), a card no longer on file (404), more than 3 removals in 10 minutes or
   * another card change in progress (409), and answers 502 when it could not check; each refusal
   * carries a message written for the member.
   *
   * @param {number|string} customerSubscriptionId
   * @param {string} paymentMethodId
   * @returns {Promise<{removed: boolean, method: MemberPaymentMethod|null, methods: MemberPaymentMethod[]}>}
   */
  const removePaymentMethod = async (customerSubscriptionId, paymentMethodId) => {
    // POST …/remove, not DELETE: the server answers both, and a POST is what the app proxy is
    // known to forward (no other /v4/api route uses DELETE through it yet).
    const response = await request({
      url: `/customer-subscriptions/${customerSubscriptionId}/payment-methods/${encodeURIComponent(paymentMethodId)}/remove`,
      method: "post",
      contentType: "application/json",
      data: JSON.stringify({}),
    });

    return {
      removed: response?.data?.removed === true,
      method: response?.data?.method || null,
      methods: Array.isArray(response?.data?.methods) ? response.data.methods : [],
    };
  };

  /**
   * Puts a skipped week back (§3.4). Allowed until that week's build has run; after that
   * the server refuses and the member needs a human.
   * @param {number|string} orderId
   * @returns {Promise<any>}
   */
  const postScheduleUnskip = async (orderId) => {
    return request({
      url: "/schedule/unskip",
      method: "post",
      contentType: "application/json",
      data: JSON.stringify({ order_id: orderId }),
    });
  };

  /**
   * Fetches items for a specific order.
   * @param {number|string} orderId
   * @returns {Promise<OrderItemsResult>}
   */
  const getOrderItems = async (orderId) => {
    const response = await request(
      {
        url: `/orders/${orderId}/items`,
      },
      { retries: 1 },
    );

    const items = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response?.items)
        ? response.items
        : Array.isArray(response?.data?.items)
          ? response.data.items
          : [];
    const order = response?.order ?? response?.data?.order ?? null;
    const billingNotice =
      response?.billing_notice ??
      response?.data?.billing_notice ??
      order?.billing_notice ??
      null;

    return {
      items,
      order,
      billing_notice: billingNotice,
      raw: response,
    };
  };

  /**
   * Builds upcoming charge rows from orders + items.
   * Item lookup failure for a single order is ignored, and remaining rows continue.
   * @param {UpcomingChargesQuery} [params]
   * @returns {Promise<UpcomingCharge[]>}
   */
  const getUpcomingCharges = async (params) => {
    const options = params || {};
    const excludedIds = new Set(
      Array.isArray(options.excludedProductIds)
        ? options.excludedProductIds.map((id) => String(id))
        : DEFAULT_EXCLUDED_PRODUCT_IDS,
    );
    const finalStatuses = new Set(
      Array.isArray(options.finalStatuses)
        ? options.finalStatuses.map((status) => String(status).toLowerCase())
        : DEFAULT_FINAL_STATUSES,
    );

    const todayDate = options.todayDate || getTodayDate();
    const orders = await getOrders({
      page: options.page ?? 0,
      limit: options.limit ?? 100,
    });

    const upcomingOrders = orders
      .filter((order) => {
        const status = String(order?.status || "").toLowerCase();
        if (finalStatuses.has(status)) {
          return false;
        }

        const chargeDate = toDateOnly(order?.charge_scheduled_at);
        return chargeDate !== "" && chargeDate >= todayDate;
      })
      .sort((left, right) => {
        const leftTime = new Date(left?.charge_scheduled_at || 0).getTime();
        const rightTime = new Date(right?.charge_scheduled_at || 0).getTime();
        return leftTime - rightTime;
      });

    const charges = [];
    for (const order of upcomingOrders) {
      let items = [];
      let itemsBillingNotice = null;
      try {
        const itemsResult = await getOrderItems(order.id);
        items = itemsResult.items;
        itemsBillingNotice = itemsResult.billing_notice;
      } catch (error) {
        console.warn(
          `Skipping next-charge item fetch for order ${order.id}`,
          error,
        );
      }

      const titles = items
        .filter((item) => {
          const productId = String(
            item?.product?.shopify_product_id ?? item?.shopify_product_id ?? "",
          );
          return !excludedIds.has(productId);
        })
        .map((item) => item?.product?.name || item?.product?.title || "")
        .filter(Boolean);

      const selectedSubtotal = toMoney(
        itemsBillingNotice?.selected_subtotal ??
          order?.billing_notice?.selected_subtotal,
      );

      // pushed/charged orders still get the Skip button, but the backend
      // rejects API skips for them — those are "late skips" that route
      // straight to the support form (emailed to the farm) instead.
      const skippableStatuses = new Set(["pending", "incomplete", "built", "editable", "pushed", "charged"]);
      const lateSkipStatuses = new Set(["pushed", "charged"]);
      const orderStatus = String(order?.status || "").toLowerCase();
      const isSkippable = skippableStatuses.has(orderStatus);
      const isLateSkipOnly = lateSkipStatuses.has(orderStatus);

      charges.push({
        orderId: order.id,
        chargeDate: toDateOnly(order?.charge_scheduled_at),
        shareName: order?.share_name || "Upcoming Share",
        status: String(order?.status || "").toLowerCase(),
        titles,
        selectedSubtotal,
        editable: !!(order.customization_state?.is_editable),
        skippable: isSkippable,
        lateSkipOnly: isLateSkipOnly,
        isOneTime: !!(order?.customer_subscription?.subscription?.is_one_time || order?.customer_subscription?.frequency === null),
        cutoff_date: String(order?.status).toLowerCase() === "pending"
          ? null
          : toDateOnly(order.customization_window?.next_order_at || order.customization_window?.blocked_at),
        locationId: order?.location_id || order?.customer_subscription?.location_id,
        customerSubscriptionId: order?.customer_subscription_id,
      });
    }

    return charges;
  };

  /**
   * Convenience wrapper for editable orders.
   * @param {{page?: number, limit?: number, status?: string}} [params]
   * @returns {Promise<any[]>}
   */
  const getEditableOrders = async (params) => {
    const options = params || {};
    return getOrders({
      page: options.page ?? 0,
      limit: options.limit ?? 20,
      status: "editable",
    });
  };

  /**
   * The member's own settings, now DB-owned (plan 0030): autorenew flags + the standing
   * delivery instructions. Replaces the old putMetafields path, which wrote Shopify
   * customer metafields that nothing backed up.
   *
   * The booleans are NULLABLE on the wire — null means "never expressed an intent", which
   * is a real third state the renewal engine reads differently from false. The CALLER
   * decides how to display null (the account page shows it as on); do not coerce here.
   *
   * @returns {Promise<{autorenew_subscription: boolean|null, autorenew_winter: boolean|null, delivery_instructions: string|null}>}
   */
  const getCustomerSettings = async () => {
    const response = await request({ url: "/customer-settings" }, { retries: 2 });
    return response?.data ?? null;
  };

  /**
   * Partial update — send only the keys being changed; omitted keys are left alone.
   * A `delivery_instructions` of "" is a deliberate CLEAR, not a no-op.
   * @param {{autorenew_subscription?: boolean, autorenew_winter?: boolean, delivery_instructions?: string}} payload
   * @returns {Promise<any>}
   */
  const putCustomerSettings = async (payload) => {
    return request(
      {
        url: "/customer-settings",
        method: "put",
        contentType: "application/json",
        data: JSON.stringify(payload),
      },
      { retries: 1 },
    );
  };

  /**
   * Fetches preference categories.
   * @returns {Promise<any[]>}
   */
  const getCategories = async () => {
    const response = await request({ url: "/categories" }, { retries: 2 });
    return Array.isArray(response?.categories) ? response.categories : [];
  };

  /**
   * Fetches customer preferences payload.
   * @returns {Promise<any>}
   */
  const getPreferences = async () => {
    return request({ url: "/preferences" }, { retries: 2 });
  };

  /**
   * Upserts a customer preference.
   * @param {any} payload
   * @returns {Promise<any>}
   */
  const putPreferences = async (payload) => {
    return request(
      {
        url: "/preferences",
        method: "put",
        contentType: "application/json",
        data: JSON.stringify(payload),
      },
      { retries: 1 },
    );
  };

  /**
   * Searches customer preferences.
   * @param {string} query
   * @returns {Promise<any>}
   */
  const searchPreferences = async (query) => {
    return request(
      {
        url: "/preferences/search",
        data: {
          q: String(query || ""),
        },
      },
      { retries: 1 },
    );
  };

  /**
   * Fetches recommendation products and normalizes legacy/new response shapes.
   * @returns {Promise<any[]>}
   */
  const getRecommendationProducts = async () => {
    const response = await request(
      { url: "/recommendations/products" },
      { retries: 1 },
    );
    if (Array.isArray(response?.products)) {
      return response.products;
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    if (Array.isArray(response?.response)) {
      return response.response;
    }

    return [];
  };

  /**
   * Fetches a subscription by Shopify product id.
   * @param {number|string} shopifyProductId
   * @returns {Promise<any>}
   */
  const getSubscriptionByProduct = async (shopifyProductId) => {
    return request(
      { url: `/subscriptions/${shopifyProductId}` },
      { retries: 1 },
    );
  };

  /**
   * Converts API errors to a safe user-facing string.
   * @param {any} error
   * @param {string} [fallbackMessage]
   * @returns {string}
   */
  const getApiErrorMessage = (error, fallbackMessage) => {
    const defaultMessage = fallbackMessage || "Error";
    const payload = error?.responseJSON || {};

    if (
      payload.code === "ORDER_NOT_EDITABLE" ||
      payload.code === "CUSTOMIZATION_WINDOW_CLOSED"
    ) {
      return (
        window.allowed_customize_message || payload.error || defaultMessage
      );
    }

    // One-time / solidarity shares have a fixed price cap with no overage charge.
    // The cap message is theme-configurable; falls back to the server's message.
    if (payload.code === "ONE_TIME_CAP_EXCEEDED") {
      return (
        window.one_time_cap_message ||
        payload.error ||
        "You've reached the limit for this share. Remove an item to add another."
      );
    }

    if (typeof payload.error === "string") {
      return payload.error;
    }

    if (typeof payload.message === "string") {
      return payload.message;
    }

    return defaultMessage;
  };

  /**
   * Shared storefront V4 backend helper.
   * Use this object instead of hardcoding backend URLs in templates/assets.
   */
  window.CGFV4Api = {
    BASE_URL,
    DEFAULT_EXCLUDED_PRODUCT_IDS,
    DEFAULT_FINAL_STATUSES,
    request,
    getCustomerSubscriptions,
    getRechargeSubscriptions,
    getCredits,
    getCreditTransactions,
    getOrders,
    getOrder,
    getOrderItems,
    getUpcomingCharges,
    getEditableOrders,
    getCustomerSettings,
    putCustomerSettings,
    getCategories,
    getPreferences,
    putPreferences,
    searchPreferences,
    getRecommendationProducts,
    getSubscriptionByProduct,
    getApiErrorMessage,
    postSkipOrder,
    getSchedule,
    postScheduleSkip,
    postScheduleUnskip,
    sendCardUpdateEmail,
    getPaymentMethods,
    changePaymentMethod,
    removePaymentMethod,
  };
})();
